import { useApolloClient } from "@apollo/client";
import { get } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { INSURANCE_PLANS } from "utils/api/graphql/queries/insurance-plans";
import { fetchPlanPrice } from "utils/helpers/planPrices";

export const formatPlanCardOption = (plan, price) => ({
  value: plan.id,
  label: price ? `${plan.name} — ${price}` : plan.name,
});

const formatPriceLabel = (computeField) => {
  if (!computeField) return null;
  if (computeField.formatted) return computeField.formatted;
  const cost = computeField.cost;
  if (cost === null || cost === undefined || cost === "") return null;
  const currency = computeField.currency || "€";
  const numeric = Number(cost);
  if (Number.isNaN(numeric)) return `${cost} ${currency}`.trim();
  return `${numeric.toFixed(2)} ${currency}`.trim();
};

const usePlanCardOptions = ({ project, enabled }) => {
  const client = useApolloClient();
  const [state, setState] = useState({ options: [], loading: false });
  const planIds = useMemo(
    () => get(project, "fields.selectedToCompare") || [],
    [project],
  );

  useEffect(() => {
    let isCurrent = true;

    if (!enabled || !project?.id || !planIds.length) {
      setState({ options: [], loading: false });
      return () => {
        isCurrent = false;
      };
    }

    const fetchOptions = async () => {
      setState((prev) => ({ ...prev, loading: true }));
      try {
        const { data: plansData } = await client.query({
          query: INSURANCE_PLANS,
          variables: {
            isIn: { id: planIds },
            take: planIds.length,
          },
          fetchPolicy: "network-only",
        });
        const plans = get(plansData, "insurancePlans.data") || [];
        const plansById = new Map(plans.map((plan) => [plan.id, plan]));
        const orderedPlans = planIds
          .map((planId) => plansById.get(planId))
          .filter(Boolean);

        const options = await Promise.all(
          orderedPlans.map(async (plan) => {
            // Spec: if price compute fails, still show plan name.
            const computeField = await fetchPlanPrice(
              client,
              project.id,
              plan.id,
            );
            return formatPlanCardOption(plan, formatPriceLabel(computeField));
          }),
        );

        if (isCurrent) setState({ options, loading: false });
      } catch (_) {
        // Keep previously loaded options when the plans query fails;
        // never replace a populated list with a silent empty one mid-flight.
        if (isCurrent) {
          setState((prev) => ({
            options: prev.options,
            loading: false,
          }));
        }
      }
    };

    fetchOptions();
    return () => {
      isCurrent = false;
    };
  }, [client, enabled, planIds, project?.id]);

  return state;
};

export default usePlanCardOptions;
